<template lang="pug">
div
  div.px-5.py-4
    div.d-flex.align-center
      div.text-xs.font-semibold.neutral700--text.ellipsis-text {{ treatment.name }}
      div.text-xs.neutral500--text.ml-2(v-if="treatment.product.sku && isInventoryItem") {{ treatment.product.sku }}
      y-chip.ml-2(v-if="checkActiveMadication(treatment)" label="Active" size="xs" chip-class="pa-1" color="green")
        template(#prepend)
          v-avatar.mr-1(size="6" color="success500")
      v-spacer
      div.text-xs.font-medium.neutral700--text.no-wrap.ml-3 {{ (treatment.price * treatment.quantity) | displayPrice($currency) }}
    div.d-flex.mt-1
      div
        div.text-xs.neutral700--text {{ getAmountPrice(treatment) }}
        div.text-xs.neutral500--text.mt-1(v-if="canPrintLabel(treatment)") {{ getTreatmentExtraData(treatment.extra_data) }}
      template(v-if="treatment.product.type === 'documents'")
        v-spacer
        template(v-if="treatment.extra_data.status === 'incomplete'")
          y-chip(label="Incomplete" size="xs" chip-class="pa-1 font-medium" color="red")
            template(#prepend)
              v-avatar.mr-1(size="6" color="danger500")
            template(#append)
              y-button.ml-2(label="Continue" outlined color="neutral400" background-color="var(--v-neutral100)" height="24" text-color="neutral700" unset-min-width size="xxs" button-class="px-2" @click="openAddMedicalDocumentDialog")
        template(v-else)
          y-button.ml-3(label="Print document" :loading="isLoading" outlined color="neutral400" background-color="var(--v-neutral100)" height="24" text-color="neutral700" prepend-icon="$yzi-file-05" icon-size="14" icon-color="neutral500" unset-min-width size="xxs" button-class="px-2" @click="onPrint(treatment)")
      template(v-if="showPrintLabel && canPrintLabel(treatment)")
        v-spacer
        div(style="align-content: center")
          y-button.ml-3(label="Print label" :loading="isLoading" outlined color="neutral400" background-color="var(--v-neutral100)" height="24" text-color="neutral700" prepend-icon="$yzi-tag-02" icon-size="14" icon-color="neutral500" unset-min-width size="xxs" button-class="px-2" @click="onPrint(treatment)")
      
    div.mt-1(v-if="treatment.product.type === 'bundle'")
      template(v-for="(bundle, idx) in treatment.bundles")
        div.d-flex.my-1(:key="`bn-${itemIndex}-${idx}`")
          v-icon.ml-2.mr-1(size="14" color="neutral700") $yzi-corner-down-right
          div
            div.text-xs.font-medium.neutral700--text.ellipsis-text {{ bundle.name }}
              span.ml-1(v-if="bundle.quantity_per_set > 1") x{{ bundle.quantity_per_set }}
            div.text-xs.neutral500--text(v-if="canPrintLabel(bundle)") {{ getTreatmentExtraData(bundle.extra_data) }}
          template(v-if="showPrintLabel && canPrintLabel(bundle)")
            v-spacer
            div(style="align-content: center")
              y-button.ml-3(label="Print label" outlined color="neutral400" background-color="var(--v-neutral100)" height="24" text-color="neutral700" prepend-icon="$yzi-tag-02" icon-size="14" icon-color="neutral500" unset-min-width size="xxs" button-class="px-2" @click="onPrint(bundle)")
  .dialogs
    add-medical-document-dialog(v-if="showAddMedicalDocumentDialog" :open.sync="showAddMedicalDocumentDialog" :treatment="documentTreatment" :diagnoses="consultation.diagnoses" @updated="updateDocument")
    edit-not-allowed-dialog(v-if="showEditNotAllowedDialog" :open.sync="showEditNotAllowedDialog" )

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AddMedicalDocumentDialog from '@/components/clinic/consultation/AddMedicalDocumentDialog.vue'
import EditNotAllowedDialog from '@/components/clinic/consultation/EditNotAllowedDialog.vue'

export default {
  components: { AddMedicalDocumentDialog, EditNotAllowedDialog },
  props: {
    treatment: {
      type: Object,
      required: true
    },
    itemIndex: {
      default: null
    },
    showPrintLabel: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isLoading: false,
      showAddMedicalDocumentDialog: false,
      showEditNotAllowedDialog: false,
      documentTreatment: null,
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      activeMedications: 'treatment/getActiveMedications',
      consultation: 'treatment/getConsultation',
      isDoctor: 'user/isDoctor',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    isInventoryItem () {
      return ['medication', 'equipment', 'consumable'].includes(this.treatment.product.type)
    },
    allowDocumentEdit () {
      if (this.company.genes.find(gene => gene.feature === 'lock_document_edit_treatment_plan') && !this.isAdminOrDoctor) return false
      return true
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    getAmountPrice (treatment) {
      let data = []
      let price = this.$options.filters.displayPrice(treatment.price, this.$currency)
      if (treatment.extra_data && treatment.extra_data.price_tier_name) { data.push(treatment.extra_data.price_tier_name) }
      if (treatment.quantity > 1) { data.push(`${price} x${treatment.quantity}`) }
      else { data.push(price) }
      return data.join(', ')
    },
    getTreatmentExtraData (extra_data) {
      let data = []
      if (extra_data.indication) { data.push(extra_data.indication) }
      if (extra_data.dosage_value && extra_data.dosage_unit) { data.push(`${extra_data.dosage_value} ${extra_data.dosage_unit}`) }
      if (extra_data.frequency) { data.push(extra_data.frequency) }
      if (extra_data.duration) { data.push(extra_data.duration) }
      if (extra_data.instruction) { data.push(extra_data.instruction) }
      if (extra_data.precaution) { data.push(extra_data.precaution) }
      return data.join(' | ')
    },
    checkActiveMadication (treatment) {
      if (treatment.product.medication && treatment.product.medication.id) {
        if (this.activeMedications.find(f => f.m_id === treatment.product.medication.id)) {
          return true
        }
      }
      return false
    },
    canPrintLabel (item) {
      if (this.hasValue(item.extra_data.indication)) {
        return true
      }
      if (this.hasValue(item.extra_data.dosage_value) && this.hasValue(item.extra_data.dosage_unit)) {
        return true
      }
      if (this.hasValue(item.extra_data.frequency)) {
        return true
      }
      if (this.hasValue(item.extra_data.duration)) {
        return true
      }
      if (this.hasValue(item.extra_data.instruction)) {
        return true
      }
      if (this.hasValue(item.extra_data.precaution)) {
        return true
      }
      return false
    },
    hasValue(value) {
      if (typeof value === 'string') {
        value = value.trim()
      }
      return value !== null && value !== undefined && value !== ''
    },
    openAddMedicalDocumentDialog () {
      if (!this.allowDocumentEdit) {
        this.showEditNotAllowedDialog = true
        return
      }
      let treatment = {
        item_id: this.treatment.id || null,
        id: this.treatment.product.id,
        name: this.treatment.product.name,
        inventory_enabled: this.treatment.product.inventory_enabled,
        inventory_qty: this.treatment.product.inventory_qty,
        type: this.treatment.product.type,
        price: this.treatment.product.price,
        medication: this.treatment.product.medication,
        document_template: this.treatment.product.document_template,
        document: { ...this.treatment.extra_data }
      }
      this.documentTreatment = treatment
      this.showAddMedicalDocumentDialog = true
    },
    async onPrint (item) {
      this.isLoading = true
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoading = false
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      const url = `${baseUrl}/clinics/${companySlug}/registrations/${this.leadPid}/preview_document?leadproduct=${item.id}&key=${key}`
      window.open(url,'_blank')
      this.isLoading = false
    },
    updateDocument (data) {
      let payload = { extra_data: { ...this.documentTreatment.extra_data, ...data } }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      const endpoint = `/clinics/${companySlug}/invoices/${this.leadPid}`
      this.$axios.patch(endpoint, { products: [{ id: this.treatment.id, ...payload }] })
        .then(resp => {
          this.updateActivityLog(endpoint, payload, resp.data, 'PATCH', resp.data.id, 'consultation', `Update document (${this.leadPid})`)
          this.$emit('updated')
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')   
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.showAddMedicalDocumentDialog = false
        })
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
</style>